import React, { useState, useEffect } from "react";
import { Button, Dialog, CircularProgress } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/pro-light-svg-icons";
import { faChevronRight } from "@fortawesome/pro-regular-svg-icons";

import { extendWithLoadingAndFail } from "App/Modules/Services";
import type { IAlarmCentralsRepository, IPartnersRepository } from "App/Modules/Repositories";
import type { IAlarmCentral } from "App/Modules/Models";

import { SearchField, jsonEqual } from "App/ReactComponents/Shared";
import "./_alarm-centrals.scss";
import { CreateAlarmCentralDialog } from "./CreateAlarmCentralDialog";

type InjectedProps = {
    mmAlarmCentralsRepository: IAlarmCentralsRepository;
    mmPartnersRepository: IPartnersRepository;
};

type AlarmCentalsProps = InjectedProps;

export const AlarmCentrals = (props: AlarmCentalsProps) => {
    const [globalFilter, setGlobalFilter] = useState("");
    const [alarmCentralsLoading, setAlarmCentralsLoading] = useState(true);
    const [isCreateAlarmCentralOpen, setCreateAlarmCentralOpen] = useState(false);
    const [alarmCentrals, setAlarmCentrals] = useState<IAlarmCentral[]>();

    const { t } = useTranslation();
    const filterFunc = (alarmCentral: IAlarmCentral) => {
        if (!globalFilter) {
            return true;
        }
        return (
            alarmCentral.name.toLowerCase().indexOf(globalFilter.toLowerCase()) > -1 ||
            alarmCentral.partner.name.toLowerCase().indexOf(globalFilter.toLowerCase()) > -1
        );
    };
    const fetchAndSetAlarmCentrals = () => {
        extendWithLoadingAndFail(props.mmAlarmCentralsRepository.getAlarmCentrals()).subscribe(
            (response) => {
                if (!response.isLoading && !jsonEqual(response.data, alarmCentrals)) {
                    setAlarmCentrals(response.data);
                    setAlarmCentralsLoading(false);
                }
            },
            (error) => {
                setAlarmCentrals(error);
                setAlarmCentralsLoading(false);
            },
        );
    };

    useEffect(() => {
        fetchAndSetAlarmCentrals();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleCreateAlarmCentralClose = () => {
        setCreateAlarmCentralOpen(false);
    };
    const handleCreateAlarmCentral = () => {
        setCreateAlarmCentralOpen(false);
        fetchAndSetAlarmCentrals();
    };
    const addNewAlarmCentral = (event) => {
        event.preventDefault();
        setCreateAlarmCentralOpen(true);
    };

    return (
        <div className="alarm-centrals card">
            <h1 className="title title--h2">{t("BeforeLogin.AlarmCentral.AlarmCentrals")}</h1>
            <div className="alarm-centrals__functions">
                <div className="alarm-centrals__filter">
                    <SearchField globalFilter={globalFilter} setGlobalFilter={setGlobalFilter} />
                </div>
                <Button
                    variant="contained"
                    size="small"
                    color="primary"
                    startIcon={<FontAwesomeIcon icon={faPlus} fixedWidth />}
                    onClick={addNewAlarmCentral}
                >
                    {t("Entities.AlarmCentral.AddAlarmCentral")}
                </Button>
            </div>
            <div className="scrollable-list card card--nested card--alt">
                <div className="scrollable-list__scroller scrollable-list__scroller--large">
                    <div className="entity-list">
                        {alarmCentralsLoading ? (
                            <div className="entity-list__item entity-list__item--loading">
                                <CircularProgress />
                            </div>
                        ) : null}
                        {alarmCentrals?.map((alarmCentral) => {
                            if (filterFunc(alarmCentral)) {
                                return (
                                    <div className="entity-list__item" key={alarmCentral.id}>
                                        <a
                                            className="entity-list__item-link entity-list__item-link--padding"
                                            href={`/#!/alarmCentrals/${alarmCentral.id}`}
                                        >
                                            <div className="entity-list__item-panel">
                                                <p className="paragraph paragraph--no-spacing">{alarmCentral.name}</p>
                                                <p className="caption caption--no-spacing">
                                                    {`${t("Entities.Partner.Partner")}: ${alarmCentral.partner.name}`}
                                                </p>
                                            </div>
                                            <div className="entity-list__item-arrow">
                                                <FontAwesomeIcon icon={faChevronRight} />
                                            </div>
                                        </a>
                                    </div>
                                );
                            }
                            return null;
                        })}
                    </div>
                </div>
            </div>
            <Dialog open={isCreateAlarmCentralOpen} maxWidth="sm">
                <CreateAlarmCentralDialog
                    onClose={() => handleCreateAlarmCentralClose()}
                    onCreateAlarmCentral={() => handleCreateAlarmCentral()}
                    mmAlarmCentralsRepository={props.mmAlarmCentralsRepository}
                    mmPartnersRepository={props.mmPartnersRepository}
                />
            </Dialog>
        </div>
    );
};
